import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  Grid,
  FormControl,
  InputLabel,
  Select,
  FormHelperText,
  Box,
  CircularProgress,
  IconButton,
  Typography,
  Divider,
  Autocomplete,
  Card,
  CardMedia,
  CardActions,
  InputAdornment,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import AddPhotoAlternateIcon from '@mui/icons-material/AddPhotoAlternate';
import DeleteIcon from '@mui/icons-material/Delete';
import { getProducts, getColors, getSizes, getProductsAutocomplete } from '../../services/productapi';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
const initialFormData = {
  sku: '',
  product: '',
  color: '',
  size: '',
  upc: '',
  stock: 0,
  stock_packed: 0,
  local_cost_price: 0.00,
  youtube: '',
  state: 'active',
  image: null,
  image_changed: false,
  image_removed: false
};

const VariantFormModal = ({ open, onClose, onSave, variant = null, isLoading = false }) => {
  const [formData, setFormData] = useState(initialFormData);
  const [errors, setErrors] = useState({});
  const [products, setProducts] = useState([]);
  const [colors, setColors] = useState([]);
  const [sizes, setSizes] = useState([]);
  const [productsLoading, setProductsLoading] = useState(false);
  const [colorsLoading, setColorsLoading] = useState(false); 
  const [sizesLoading, setSizesLoading] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [searchTimeout, setSearchTimeout] = useState(null);
  
  // Image preview state
  const [imagePreview, setImagePreview] = useState(null);
  
  // Fetch reference data for dropdowns
  useEffect(() => {
    const fetchReferenceData = async () => {
      try {
        setProductsLoading(true);
        setColorsLoading(true);
        setSizesLoading(true);

        const [productsResponse, colorsResponse, sizesResponse] = await Promise.all([
          getProductsAutocomplete(), // Use autocomplete endpoint for all products
          getColors(),
          getSizes()
        ]);

        // Products from autocomplete - already in simple format { id, name }
        setProducts(productsResponse.data || []);

        // Colors are not paginated, but handle both formats just to be safe
        if (colorsResponse.data.results) {
          setColors(colorsResponse.data.results);
        } else {
          setColors(colorsResponse.data);
        }

        // Sizes are not paginated, but handle both formats just to be safe
        if (sizesResponse.data.results) {
          setSizes(sizesResponse.data.results);
        } else {
          setSizes(sizesResponse.data);
        }

        setProductsLoading(false);
        setColorsLoading(false);
        setSizesLoading(false);
      } catch (error) {
        console.error('Error fetching reference data:', error);
        setProductsLoading(false);
        setColorsLoading(false);
        setSizesLoading(false);
      }
    };

    if (open) {
      fetchReferenceData();
    }
  }, [open]);  
  
  // If editing, populate form with variant data
  useEffect(() => {
    if (variant) {
      // When editing, log the data to see what fields we have
      console.log('Editing variant with raw data:', variant);

      // Get product ID from variant
      const productId = variant.product;

      if (!productId) {
        console.warn('Warning: Variant is missing product ID which is required by the API:', variant);
      }

      // First update the form data with all variant fields
      setFormData({
        sku: variant.sku || '',
        product: productId || '', // Make sure this is always set
        color: variant.color || '',
        size: variant.size || '',
        upc: variant.upc || '',
        stock: variant.stock || 0,
        stock_packed: variant.stock_packed || 0,
        local_cost_price: variant.local_cost_price || 0.00,
        youtube: variant.youtube || '',
        state: variant.state || 'active',
        image: null, // Will be handled separately for file uploads
        image_changed: false, // Track if image has been changed
        image_removed: false   // Track if existing image should be removed
      });
      
      // Set image preview if variant has an image
      if (variant.image) {
        setImagePreview(variant.image);
        console.log('Setting image preview:', variant.image);
      } else {
        setImagePreview(null);
      }

      // Then handle the selected product for the autocomplete
      if (productId) {
        // Create a product object with all available information
        const productName = variant.product_name || `Product ${productId}`;
        const productObject = {
          id: productId,
          name: productName
        };

        // Set the selected product
        setSelectedProduct(productObject);

        // Make sure this product is in the options list
        setProducts(prevProducts => {
          // Check if product already exists in the list
          const exists = prevProducts.some(p => p.id === productId);
          if (!exists) {
            // Add at the beginning of the list
            return [productObject, ...prevProducts];
          }
          return prevProducts;
        });

        console.log(`Set selected product: ID=${productId}, Name=${productName}`);
      } else {
        setSelectedProduct(null);
      }
    } else {
      // Reset for new variant
      setFormData(initialFormData);
      setSelectedProduct(null);
      setImagePreview(null);
    }

    // Clear errors when modal opens/closes or variant changes
    setErrors({});
  }, [variant, open]); // Remove products dependency to prevent loops

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (searchTimeout) {
        clearTimeout(searchTimeout);
      }
    };
  }, [searchTimeout]);

  // Handle image selection
  const handleImageChange = (e) => {
    const file = e.target.files[0];
    
    if (file) {
      // Validate file type (e.g., only allow images)
      if (!file.type.startsWith('image/')) {
        setErrors({
          ...errors,
          image: 'Uploaded file is not an image'
        });
        return;
      }

      // Validate file size (e.g., max 2MB)
      if (file.size > 2 * 1024 * 1024) {
        setErrors({
          ...errors,
          image: 'Image file size exceeds 2MB'
        });
        return;
      }
      
      // Update form data with the file
      setFormData({
        ...formData,
        image: file,
        image_changed: true // Flag that image was changed
      });
      
      // Create a preview URL for the image
      const previewUrl = URL.createObjectURL(file);
      setImagePreview(previewUrl);
      
      // Clear any existing image error
      if (errors.image) {
        setErrors({
          ...errors,
          image: null
        });
      }
    }
  };
  
  // Handle image removal
  const handleRemoveImage = () => {
    // If we're in edit mode and the image is a URL, track that we want to remove it
    const isRemovingExistingImage = variant && variant.image === imagePreview;
    
    setFormData({
      ...formData,
      image: null,
      image_changed: true, // Flag that image was changed
      image_removed: isRemovingExistingImage // Flag that we're removing an existing image
    });
    setImagePreview(null);
  };
  
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });

    // Clear error for this field if it was previously set
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: null
      });
    }
  }; 
  
  const handleProductSearch = async (query = '') => {
    // Clear any existing timeout
    if (searchTimeout) {
      clearTimeout(searchTimeout);
    }

    // Handle search based on query length
    if (query.length >= 2 || query.length === 0) {
      const searchFunction = async () => {
        try {
          setProductsLoading(true);
          const response = await getProductsAutocomplete(query);

          // Ensure we're getting valid data
          if (response && response.data) {
            // If we're editing a product, make sure the current selection stays in the list
            if (selectedProduct && selectedProduct.id) {
              const productExists = response.data.some(p => p.id === selectedProduct.id);

              if (!productExists) {
                // Keep the selected product in the list
                setProducts([selectedProduct, ...response.data]);
              } else {
                setProducts(response.data);
              }
            } else {
              setProducts(response.data);
            }
          } else {
            console.warn('Received invalid product data from API:', response);
            setProducts([]);
          }
        } catch (error) {
          console.error('Error searching products:', error);
        } finally {
          setProductsLoading(false);
        }
      };

      if (query.length >= 2) {
        // Debounce searches for typed text
        const timeout = setTimeout(searchFunction, 300);
        setSearchTimeout(timeout);
      } else {
        // Immediate search for empty/reset queries
        searchFunction();
      }
    }
  };
  
  const handleProductChange = (event, newValue) => {
    // Handle both objects and IDs safely
    const productId = newValue?.id || '';

    // Update both the selected product and the form data
    setSelectedProduct(newValue);
    setFormData({
      ...formData,
      product: productId
    });

    // Clear product error if it was set
    if (errors.product) {
      setErrors({
        ...errors,
        product: null
      });
    }

    console.log(`Product selected: ${newValue ? `ID: ${productId}, Name: ${newValue.name}` : 'None'}`);
  };

  const validateForm = () => {
    const newErrors = {};

    // Required fields
    if (!formData.sku) newErrors.sku = 'SKU is required';
    if (!formData.product) newErrors.product = 'Product is required';

    // Validate numeric fields
    if (formData.stock && isNaN(Number(formData.stock))) {
      newErrors.stock = 'Stock must be a number';
    }
    if (formData.stock_packed && isNaN(Number(formData.stock_packed))) {
      newErrors.stock_packed = 'Stock packed must be a number';
    }
    if (formData.local_cost_price && isNaN(Number(formData.local_cost_price))) {
      newErrors.local_cost_price = 'Local cost price must be a number';
    }


    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }; 
  
  const handleSubmit = () => {
    if (validateForm()) {
      // Use FormData for multipart/form-data submissions (needed for file uploads)
      const formDataToSubmit = new FormData();
      
      // Add basic fields
      formDataToSubmit.append('sku', formData.sku);
      formDataToSubmit.append('product', formData.product);
      formDataToSubmit.append('stock', parseInt(formData.stock, 10));
      formDataToSubmit.append('stock_packed', parseInt(formData.stock_packed, 10));
      formDataToSubmit.append('local_cost_price', parseFloat(formData.local_cost_price).toFixed(2));
      formDataToSubmit.append('youtube', formData.youtube);
      formDataToSubmit.append('state', formData.state);
      
      // Add optional fields only if they have values
      if (formData.color) formDataToSubmit.append('color', formData.color);
      if (formData.size) formDataToSubmit.append('size', formData.size);
      if (formData.upc) formDataToSubmit.append('upc', formData.upc);
      
      // Handle image upload - only add if it's a new File object
      if (formData.image instanceof File) {
        formDataToSubmit.append('image', formData.image);
      }
      
      // If we're explicitly removing an existing image in edit mode
      if (variant && formData.image_removed) {
        // Send empty string to clear the image on the server
        formDataToSubmit.append('image', '');
        console.log('Removing existing image');
      }

      // Log the final data being sent
      console.log(`Submitting variant data (${variant ? 'edit' : 'add'} mode)`);
      
      // Verify product is set before submitting
      if (!formData.product) {
        setErrors({
          ...errors,
          product: 'Product is required by the API'
        });
        return;
      }

      onSave(formDataToSubmit, variant ? 'edit' : 'add');
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{ sx: { borderRadius: 2 } }}
    >
      <DialogTitle sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        bgcolor: '#f8f9fa',
        p: 2
      }}>
        <Typography variant="h6">
          {variant ? 'Edit Variant' : 'Add New Variant'}
        </Typography>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <Divider />

      <DialogContent sx={{ p: 3 }}>        
        <Grid container spacing={3} >
          {/* Left side - form fields */}
          <Grid item xs={12} md={6} sx={{ width: '400px' }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <TextField
                label="SKU"
                name="sku"
                value={formData.sku}
                onChange={handleChange}
                fullWidth
                required
                error={!!errors.sku}
                helperText={errors.sku}
                size="small"
              />
              <Autocomplete
                fullWidth
                size="small"
                options={products}
                getOptionLabel={(option) => {
                  // Handle string or object inputs and prevent undefined errors
                  if (typeof option === 'string') return option;
                  return option?.name || '';
                }}
                isOptionEqualToValue={(option, value) => {
                  // Handle edge cases to prevent selection bugs
                  if (!option || !value) return false;
                  return option.id === value.id;
                }}
                value={selectedProduct}
                onChange={handleProductChange}
                onInputChange={(event, newInputValue, reason) => {
                  // Only search when user is typing
                  if (reason === 'input') {
                    handleProductSearch(newInputValue);
                  } else if (reason === 'clear') {
                    // When cleared, reset selection
                    setSelectedProduct(null);
                    setFormData({
                      ...formData,
                      product: ''
                    });
                    // Reset to initial product list
                    handleProductSearch('');
                  }
                }}
                onOpen={() => {
                  // Load products when dropdown opens, if not already loaded
                  if (products.length === 0 && !productsLoading) {
                    handleProductSearch('');
                  }
                }}
                loading={productsLoading}
                clearOnBlur={false}
                selectOnFocus
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Product *"
                    error={!!errors.product}
                    helperText={errors.product}
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {productsLoading ? <CircularProgress color="inherit" size={20} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
                renderOption={(props, option) => (
                  <Box component="li" {...props}>
                    {option.name || 'Unknown product'}
                  </Box>
                )}
                noOptionsText="No products found"
              />
              <Autocomplete
                fullWidth
                size="small"
                options={colors}
                getOptionLabel={(option) => {
                  if (typeof option === 'string') return option;
                  return option?.name || '';
                }}
                isOptionEqualToValue={(option, value) => {
                  if (!option || !value) return false;
                  return option.id === value.id;
                }}
                value={colors.find(color => color.id === formData.color) || null}
                onChange={(event, newValue) => {
                  setFormData({
                    ...formData,
                    color: newValue ? newValue.id : ''
                  });
                  
                  // Clear color error if it was set
                  if (errors.color) {
                    setErrors({
                      ...errors,
                      color: null
                    });
                  }
                }}
                loading={colorsLoading}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Color"
                    error={!!errors.color}
                    helperText={errors.color}
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {colorsLoading ? <CircularProgress color="inherit" size={20} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
                renderOption={(props, option) => (
                  <Box component="li" {...props}>
                    {option.name}
                  </Box>
                )}
                noOptionsText="No colors found"
              />
              <Autocomplete
                fullWidth
                size="small"
                options={sizes}
                getOptionLabel={(option) => {
                  if (typeof option === 'string') return option;
                  return option?.name || '';
                }}
                isOptionEqualToValue={(option, value) => {
                  if (!option || !value) return false;
                  return option.id === value.id;
                }}
                value={sizes.find(size => size.id === formData.size) || null}
                onChange={(event, newValue) => {
                  setFormData({
                    ...formData,
                    size: newValue ? newValue.id : ''
                  });
                  
                  // Clear size error if it was set
                  if (errors.size) {
                    setErrors({
                      ...errors,
                      size: null
                    });
                  }
                }}
                loading={sizesLoading}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Size"
                    error={!!errors.size}
                    helperText={errors.size}
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {sizesLoading ? <CircularProgress color="inherit" size={20} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
                renderOption={(props, option) => (
                  <Box component="li" {...props}>
                    {option.name}
                  </Box>
                )}
                noOptionsText="No sizes found"
              />

              <TextField
                label="UPC"
                name="upc"
                value={formData.upc}
                onChange={handleChange}
                fullWidth
                size="small"
              />

              <FormControl fullWidth size="small">
                <InputLabel id="status-label">Status</InputLabel>
                <Select
                  labelId="status-label"
                  name="state"
                  value={formData.state}
                  onChange={handleChange}
                  label="Status"
                >
                  <MenuItem value="active">Active</MenuItem>
                  <MenuItem value="inactive">Inactive</MenuItem>
                </Select>
              </FormControl>

              <TextField
                label="Stock"
                name="stock"
                type="number"
                value={formData.stock}
                onChange={handleChange}
                fullWidth
                error={!!errors.stock}
                helperText={errors.stock}
                size="small"
              />
              <TextField
                label="Local Cost Price"
                name="local_cost_price"
                type="number"
                value={formData.local_cost_price}
                onChange={handleChange}
                fullWidth
                error={!!errors.local_cost_price}
                helperText={errors.local_cost_price}
                size="small"
              />
              <TextField
                label="Stock Packed"
                name="stock_packed"
                type="number"
                value={formData.stock_packed}
                onChange={handleChange}
                fullWidth
                error={!!errors.stock_packed}
                helperText={errors.stock_packed}
                size="small"
              />
              <TextField
  label="YouTube Link"
  name="youtube"
  value={formData.youtube}
  onChange={handleChange}
  fullWidth
  size="small"
  placeholder="https://www.youtube.com/watch?v=example"
  InputProps={{
    endAdornment: (
      <InputAdornment position="end">
        <IconButton
          onClick={() => {
            if (formData.youtube.startsWith("http")) {
              window.open(formData.youtube, "_blank");
            }
          }}
          disabled={!formData.youtube.startsWith("http")}
        >
          <OpenInNewIcon />
        </IconButton>
      </InputAdornment>
    ),
  }}
/>
              
          
            </Box>          
          </Grid>
          
          {/* Right side - image display/upload */}
          <Grid item xs={12} md={6}>
            <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                          
              <Card variant="outlined" sx={{ 
                display: 'flex', 
                flexDirection: 'column',
                height: '100%', 
                width: '400px',
                minHeight: 340,
                justifyContent: imagePreview ? 'space-between' : 'center',
                alignItems: 'center'
              }}>
                {imagePreview ? (
                  <CardMedia
                    component="img"                    image={imagePreview}
                    alt="Product variant"
                    sx={{ 
                      width: '100%',
                      height: 340,
                      objectFit: 'contain',
                      p: 1
                    }}
                  />
                ) : (
                  <Box sx={{ 
                    display: 'flex', 
                    flexDirection: 'column', 
                    alignItems: 'center', 
                    justifyContent: 'center',
                    p: 2, 
                    height: '100%'
                  }}>
                    <AddPhotoAlternateIcon sx={{ fontSize: 60, color: 'text.secondary', mb: 1 }} />
                    <Typography variant="body2" color="text.secondary" align="center">
                      No image uploaded
                    </Typography>
                  </Box>
                )}
                <CardActions sx={{ width: '100%', justifyContent: 'center', p: 2 }}>
                  <input
                    accept="image/*"
                    style={{ display: 'none' }}
                    id="image-upload-button"
                    type="file"
                    onChange={handleImageChange}
                  />
                  <label htmlFor="image-upload-button">
                    <Button 
                      variant="outlined" 
                      component="span" 
                      startIcon={<AddPhotoAlternateIcon />}
                      size="small"
                    >
                      {imagePreview ? 'Change Image' : 'Upload Image'}
                    </Button>
                  </label>
                  {imagePreview && (
                    <Button 
                      color="error" 
                      startIcon={<DeleteIcon />} 
                      onClick={handleRemoveImage}
                      size="small"
                      sx={{ ml: 1 }}
                    >
                      Remove
                    </Button>
                  )}
                </CardActions>
              </Card>
              {errors.image && (
                <Typography variant="caption" color="error">
                  {errors.image}
                </Typography>
              )}
            </Box>
          </Grid>
        </Grid>
      </DialogContent>

      <Divider />

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button
          onClick={onClose}
          variant="outlined"
          sx={{ textTransform: 'none' }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={isLoading}
          sx={{
            bgcolor: '#3f51b5',
            '&:hover': { bgcolor: '#303f9f' },
            textTransform: 'none'
          }}
          startIcon={isLoading ? <CircularProgress size={20} /> : null}
        >
          {isLoading ? 'Saving...' : variant ? 'Update Variant' : 'Add Variant'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default VariantFormModal;
